import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  Driver,
  DriverListResponse,
  DriverListParams,
  DriverInput,
  ExpiringDriverDocsResponse,
} from '@/lib/types';

export async function fetchDrivers(params: DriverListParams): Promise<DriverListResponse> {
  const { data } = await apiClient.get<DriverListResponse>('/api/v1/drivers', { params });
  return data;
}

export async function fetchDriver(id: string): Promise<Driver> {
  const { data } = await apiClient.get<{ driver: Driver }>(`/api/v1/drivers/${id}`);
  return data.driver;
}

export async function createDriver(input: DriverInput): Promise<Driver> {
  const { data } = await apiClient.post<{ driver: Driver }>('/api/v1/drivers', input);
  return data.driver;
}

export async function updateDriver(id: string, input: Partial<DriverInput>): Promise<Driver> {
  const { data } = await apiClient.patch<{ driver: Driver }>(`/api/v1/drivers/${id}`, input);
  return data.driver;
}

export async function fetchExpiringDriverDocs(windowDays: number = 30): Promise<ExpiringDriverDocsResponse> {
  const { data } = await apiClient.get<ExpiringDriverDocsResponse>(
    '/api/v1/drivers/expiring-documents',
    { params: { windowDays } }
  );
  return data;
}

export function useDrivers(params: DriverListParams) {
  return useQuery({
    queryKey: ['drivers', params],
    queryFn: () => fetchDrivers(params),
    placeholderData: (prev) => prev,
  });
}

export function useDriver(id: string | null | undefined) {
  return useQuery({
    queryKey: ['driver', id],
    queryFn: () => fetchDriver(id!),
    enabled: !!id,
  });
}

export function useCreateDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDriver,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers'] }),
  });
}

export function useUpdateDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<DriverInput> }) =>
      updateDriver(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['drivers'] });
      qc.setQueryData(['driver', updated.id], updated);
    },
  });
}

/** A driver record one of the data-quality checks caught. */
export interface DqDriver {
  id: string;
  code: string;
  fullNameEn: string;
  iqamaNumber: string | null;
  onTruck: number;
}

export interface DriverDataQuality {
  placeholderIds: DqDriver[];
  /** The same person twice, grouped by name. Merge candidates. */
  duplicateGroups: Array<{ key: string; drivers: DqDriver[] }>;
  /** Same name, every record holding a real iqama. DIFFERENT PEOPLE. */
  sameNameDifferentPeople: Array<{ key: string; drivers: DqDriver[] }>;
  /** One number, several names. Information - not a fault, never a merge. */
  sharedPhones: Array<{ phone: string; drivers: DqDriver[] }>;
  summary: {
    placeholders: number;
    placeholdersOnTruck: number;
    duplicateGroups: number;
    duplicateRecords: number;
    sharedPhones: number;
    sameNameDifferentPeople: number;
    affectedRecords: number;
  };
}

export async function fetchDriverDataQuality(): Promise<DriverDataQuality> {
  const { data } = await apiClient.get<DriverDataQuality>('/api/v1/drivers/data-quality');
  return data;
}

export function useDriverDataQuality() {
  return useQuery({
    queryKey: ['driver-data-quality'],
    queryFn: fetchDriverDataQuality,
    // Import fallout, not live telemetry - it does not change minute to minute.
    staleTime: 5 * 60 * 1000,
  });
}

export function useExpiringDriverDocs(windowDays: number = 30) {
  return useQuery({
    queryKey: ['expiring-driver-docs', windowDays],
    queryFn: () => fetchExpiringDriverDocs(windowDays),
    staleTime: 60 * 1000,
  });
}
